import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Producto } from '../../domain/entities/Producto';
import { EnvioNacionalForm, EnvioNacionalColombiaPayload } from '../../presentation/components/EnvioNacionalModal/EnvioNacionalModal.types';

const generateCartItemId = (): string => {
  if (globalThis.crypto?.randomUUID !== undefined) {
    return globalThis.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

export interface CartItem {
  cartItemId: string;
  producto: Producto;
  cantidad: number;
  envioNacional?: EnvioNacionalForm;
  envioNacionalColombia?: EnvioNacionalColombiaPayload;
  /** Formulario del wizard CO. Solo frontend; se omite en POST /ventas. */
  envioNacionalForm?: EnvioNacionalForm;
}

export interface CartContextType {
  carrito: CartItem[];
  agregarAlCarrito: (producto: Producto, envioNacional?: EnvioNacionalForm) => void;
  agregarGuiaColombia: (producto: Producto, payload: EnvioNacionalColombiaPayload, form: EnvioNacionalForm) => void;
  editarEnvioNacional: (cartItemId: string, form: EnvioNacionalForm, producto?: Producto) => void;
  editarGuiaColombia: (cartItemId: string, payload: EnvioNacionalColombiaPayload, form: EnvioNacionalForm, producto?: Producto) => void;
  actualizarCantidad: (idProducto: string, delta: number) => void;
  setCantidad: (idProducto: string, cantidad: number) => void;
  eliminarDelCarrito: (cartItemId: string) => void;
  limpiarCarrito: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  // Inicializar el carrito leyendo de localStorage para que persista incluso si se recarga la página
  const [carrito, setCarrito] = useState<CartItem[]>(() => {
    try {
      const savedCart = localStorage.getItem('pos_cart');
      if (!savedCart) return [];
      const parsed = JSON.parse(savedCart) as CartItem[];
      return parsed.map(item => ({
        ...item,
        cartItemId: item.cartItemId ?? generateCartItemId(),
      }));
    } catch {
      return [];
    }
  });

  // Cada vez que el carrito cambie, guardarlo en localStorage
  useEffect(() => {
    if (carrito.length > 0) {
      localStorage.setItem('pos_cart', JSON.stringify(carrito));
    } else {
      localStorage.removeItem('pos_cart');
    }
  }, [carrito]);

  const agregarAlCarrito = useCallback((producto: Producto, envioNacional?: EnvioNacionalForm) => {
    setCarrito(prev => {
      // Los items de envío nacional siempre se agregan como entradas independientes
      if (envioNacional) {
        return [...prev, { cartItemId: generateCartItemId(), producto, cantidad: 1, envioNacional }];
      }
      const itemExistente = prev.find(
        item => item.producto.id === producto.id && !item.envioNacional && !item.envioNacionalColombia
      );
      if (itemExistente) {
        return prev.map(item =>
          item.producto.id === producto.id && !item.envioNacional && !item.envioNacionalColombia
            ? { ...item, cantidad: item.cantidad + 1 }
            : item
        );
      }
      return [...prev, { cartItemId: generateCartItemId(), producto, cantidad: 1 }];
    });
  }, []);

  const agregarGuiaColombia = useCallback((
    producto: Producto,
    payload: EnvioNacionalColombiaPayload,
    form: EnvioNacionalForm,
  ) => {
    setCarrito(prev => [
      ...prev,
      {
        cartItemId: generateCartItemId(),
        producto,
        cantidad: 1,
        envioNacionalColombia: payload,
        envioNacionalForm: form,
      },
    ]);
  }, []);

  const editarEnvioNacional = useCallback((cartItemId: string, form: EnvioNacionalForm, producto?: Producto) => {
    setCarrito(prev =>
      prev.map(item => {
        if (item.cartItemId === cartItemId) {
          return {
            ...item,
            envioNacional: form,
            producto: producto ?? item.producto,
          };
        }
        return item;
      })
    );
  }, []);

  const editarGuiaColombia = useCallback((
    cartItemId: string,
    payload: EnvioNacionalColombiaPayload,
    form: EnvioNacionalForm,
    producto?: Producto,
  ) => {
    setCarrito(prev =>
      prev.map(item => {
        if (item.cartItemId === cartItemId) {
          return {
            ...item,
            envioNacionalColombia: payload,
            envioNacionalForm: form,
            producto: producto ?? item.producto,
          };
        }
        return item;
      })
    );
  }, []);

  const actualizarCantidad = useCallback((cartItemId: string, delta: number) => {
    setCarrito(prev => prev.map(item => {
      if (item.cartItemId === cartItemId) {
        const nuevaCantidad = item.cantidad + delta;
        return { ...item, cantidad: Math.max(0, nuevaCantidad) };
      }
      return item;
    }).filter(item => item.cantidad > 0));
  }, []);

  const setCantidad = useCallback((cartItemId: string, cantidad: number) => {
    setCarrito(prev => prev.map(item =>
      item.cartItemId === cartItemId ? { ...item, cantidad } : item
    ));
  }, []);

  const eliminarDelCarrito = useCallback((cartItemId: string) => {
    setCarrito(prev => prev.filter(item => item.cartItemId !== cartItemId));
  }, []);

  const limpiarCarrito = useCallback(() => {
    setCarrito([]);
    localStorage.removeItem('pos_cart');
  }, []);

  const value = useMemo(() => ({
    carrito, agregarAlCarrito, agregarGuiaColombia, editarEnvioNacional, editarGuiaColombia,
    actualizarCantidad, setCantidad, eliminarDelCarrito, limpiarCarrito,
  }), [carrito, agregarAlCarrito, agregarGuiaColombia, editarEnvioNacional, editarGuiaColombia,
    actualizarCantidad, setCantidad, eliminarDelCarrito, limpiarCarrito]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Hook personalizado para usar el carrito fácilmente en cualquier componente
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart debe ser usado dentro de un CartProvider');
  }
  return context;
};
